import { useEffect, useState } from "react";
import { LayoutGrid, Mail, Globe, Shield, Search, Flame, LockKeyhole, ShieldAlert, ShieldCheck, Radio, Ticket } from "lucide-react";
import { theme, card } from "../theme";
import { IndexTabs } from "../components/Drawing";
import { api } from "../api/client";
import { TIER_NAMES } from "../components/TierLockModal";
import { CLIENT_MIN_TIER } from "../nav/clientNav";
import { ConnectGooglePage } from "./ConnectGoogle";
import { ConnectStoragePage } from "./ConnectStorage";
import { ConnectM365Page } from "./ConnectM365";
import { useCurrentUser } from "../auth/useCurrentUser";
import { ConnectIntunePage } from "./ConnectIntune";
import { ConnectSentinelPage } from "./ConnectSentinel";
import { ConnectFirewallPage } from "./ConnectFirewall";
import { ConnectM365SecurityPage } from "./ConnectM365Security";
import { ConnectAwsPage } from "./ConnectAws";
import { ConnectGcpPage } from "./ConnectGcp";
import { ConnectSiemPage } from "./ConnectSiem";
import { ConnectTicketingPage } from "./ConnectTicketing";

import { cbSafe } from "../lib/colourBlind";
type Tab = "microsoft" | "m365" | "google" | "intune" | "sentinel" | "firewall" | "m365security" | "aws" | "gcp" | "siem" | "ticketing";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "microsoft",    label: "Microsoft",    icon: <LayoutGrid size={14} /> },
  { id: "m365",         label: "Microsoft 365 (Outlook, Teams)", icon: <Mail size={14} /> },
  { id: "google",       label: "Google",       icon: <Globe size={14} /> },
  { id: "intune",       label: "Intune",       icon: <Shield size={14} /> },
  { id: "sentinel",     label: "Sentinel",     icon: <Search size={14} /> },
  { id: "firewall",     label: "Firewalls",    icon: <Flame size={14} /> },
  { id: "m365security", label: "M365 Security", icon: <LockKeyhole size={14} /> },
  { id: "aws",          label: "AWS",           icon: <ShieldAlert size={14} /> },
  { id: "gcp",          label: "GCP",           icon: <ShieldCheck size={14} /> },
  { id: "siem",         label: "SIEM Forwarding", icon: <Radio size={14} /> },
  { id: "ticketing",    label: "Ticketing",     icon: <Ticket size={14} /> },
];

const VALID_TABS = new Set<Tab>(TABS.map((t) => t.id));

// Connected storage (Microsoft 365 / OneDrive / SharePoint / Google Drive /
// Blob) comes with the Documents tier; the security connectors need
// Self-Service + AI — the same split the API enforces (requireTier).
const SECURITY_TABS = new Set<Tab>(["intune", "sentinel", "firewall", "m365security", "aws", "gcp", "siem", "ticketing"]);
const SECURITY_MIN_TIER = CLIENT_MIN_TIER.securityConnectors;

function getInitialTab(): Tab {
  const param = new URLSearchParams(window.location.search).get("tab");
  return param && VALID_TABS.has(param as Tab) ? (param as Tab) : "microsoft";
}

type ConnStatus = "connected" | "attention" | "disconnected" | "unknown";

function StatusDot({ status }: { status: ConnStatus }) {
  const color = status === "connected" ? cbSafe("#22c55e") : status === "attention" ? "var(--c-warning)" : "var(--c-border)";
  return (
    <span style={{
      display: "inline-block",
      width: 7, height: 7,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
      boxShadow: status === "connected" ? cbSafe("0 0 0 2px #22c55e33") : status === "attention" ? "0 0 0 2px var(--c-warningBg)" : "none",
    }} />
  );
}

export function ConnectionsPage() {
  const { user } = useCurrentUser();
  // Every connection is made and changed by integration administrators (the server enforces integrations:admin).
  const readOnly = !!user && !user.permissions?.includes("integrations:admin");
  const [active, setActive] = useState<Tab>(getInitialTab);
  const [statuses, setStatuses] = useState<Record<Tab, ConnStatus>>({
    microsoft: "unknown", m365: "unknown", google: "unknown", intune: "unknown", sentinel: "unknown",
    firewall: "unknown", m365security: "unknown", aws: "unknown", gcp: "unknown", siem: "unknown", ticketing: "unknown",
  });
  // null until the profile answers; nothing is locked until then.
  const [tierLevel, setTierLevel] = useState<number | null>(null);
  useEffect(() => {
    api.get<{ organization?: { serviceTier?: { level?: number } } }>("/organizations/profile")
      .then((p) => setTierLevel(p.organization?.serviceTier?.level ?? null))
      .catch(() => setTierLevel(null));
  }, []);
  const securityLocked = tierLevel !== null && tierLevel < SECURITY_MIN_TIER;

  useEffect(() => {
    const checks: Array<[Tab, () => Promise<ConnStatus>]> = [
      // A stored Microsoft sign-in the platform can no longer use is neither connected nor disconnected.
      ["microsoft",    () => api.get<{ provider: string | null; authError: unknown }>("/storage/status").then((d) => !d.provider ? "disconnected" : d.authError ? "attention" : "connected").catch(() => "unknown")],
      ["m365",         () => api.get<{ connected: boolean }>("/m365/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["google",       () => api.get<{ connected: boolean }>("/google/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["intune",       () => api.get<{ connected: boolean }>("/intune/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["sentinel",     () => api.get<{ connected: boolean }>("/sentinel/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["firewall",     () => api.get<{ connections: unknown[] }>("/firewall").then((d) => d.connections.length > 0 ? "connected" : "disconnected").catch(() => "unknown")],
      ["m365security", () => api.get<{ connected: boolean }>("/m365-security/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["aws",          () => api.get<{ connected: boolean }>("/aws/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["gcp",          () => api.get<{ connected: boolean }>("/gcp/status").then((d) => d.connected ? "connected" : "disconnected").catch(() => "unknown")],
      ["siem",         () => api.get<{ enabled: boolean }>("/siem/status").then((d) => d.enabled ? "connected" : "disconnected").catch(() => "unknown")],
      ["ticketing",    () => api.get<{ connection: { lastError: string | null } | null }>("/ticketing/status").then((d) => !d.connection ? "disconnected" : d.connection.lastError ? "attention" : "connected").catch(() => "unknown")],
    ];
    Promise.allSettled(checks.map(async ([tab, fn]) => ({ tab, status: await fn() }))).then((results) => {
      const next: Partial<Record<Tab, ConnStatus>> = {};
      for (const r of results) {
        if (r.status === "fulfilled") next[r.value.tab] = r.value.status;
      }
      setStatuses((prev) => ({ ...prev, ...next }));
    });
  }, []);

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Connections
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Connect third-party services to sync documents, mirror evidence, and enable calendar, SIEM, ticketing and firewall integrations.
      </p>

      {/* Tab bar */}
      <IndexTabs
        value={active}
        onChange={setActive}
        style={{ marginBottom: 24 }}
        items={TABS.map((t) => ({
          id: t.id,
          label: (
            <>
              <span style={{ display: "inline-flex" }}>{t.icon}</span>
              {t.label}
              {securityLocked && SECURITY_TABS.has(t.id)
                ? <span title={`Requires ${TIER_NAMES[SECURITY_MIN_TIER]}`} style={{ fontSize: 10, color: theme.color.faint }}>L{SECURITY_MIN_TIER}</span>
                : <StatusDot status={statuses[t.id]} />}
            </>
          ),
        }))}
      />

      {readOnly ? (
        <div style={{ ...card, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>Connections are managed by integration administrators</div>
          <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6, maxWidth: 520 }}>
            You can see from the dots above which services are connected. To connect or change one, ask an organisation administrator.
          </div>
        </div>
      ) : securityLocked && SECURITY_TABS.has(active) ? (
        <div style={{ ...card, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
            Security integrations need {TIER_NAMES[SECURITY_MIN_TIER]}
          </div>
          <div style={{ fontSize: 13, color: theme.color.subtle, lineHeight: 1.6, maxWidth: 520 }}>
            Intune, Sentinel, firewalls, M365 Security, AWS Security Hub, GCP, SIEM forwarding and ticketing are included from
            {" "}{TIER_NAMES[SECURITY_MIN_TIER]} (tier {SECURITY_MIN_TIER}) upwards, each connection at its published rate.
            Connected storage on the Microsoft and Google tabs is part of your current tier. Request a tier change from My Services.
          </div>
        </div>
      ) : null}

      {/* Tab panels — rendered but hidden so each page keeps its own state */}
      {!readOnly && (<>
      <div style={{ display: active === "microsoft"    ? "block" : "none" }}><ConnectStoragePage /></div>
      <div style={{ display: active === "m365"         ? "block" : "none" }}><ConnectM365Page /></div>
      <div style={{ display: active === "google"       ? "block" : "none" }}><ConnectGooglePage /></div>
      {!securityLocked && (
        <>
          <div style={{ display: active === "intune"       ? "block" : "none" }}><ConnectIntunePage /></div>
          <div style={{ display: active === "sentinel"     ? "block" : "none" }}><ConnectSentinelPage /></div>
          <div style={{ display: active === "firewall"     ? "block" : "none" }}><ConnectFirewallPage /></div>
          <div style={{ display: active === "m365security" ? "block" : "none" }}><ConnectM365SecurityPage /></div>
          <div style={{ display: active === "aws"          ? "block" : "none" }}><ConnectAwsPage /></div>
          <div style={{ display: active === "gcp"          ? "block" : "none" }}><ConnectGcpPage /></div>
          <div style={{ display: active === "siem"         ? "block" : "none" }}><ConnectSiemPage /></div>
          <div style={{ display: active === "ticketing"    ? "block" : "none" }}><ConnectTicketingPage /></div>
        </>
      )}
      </>)}
    </div>
  );
}
